import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import type { LeapBotContextSnapshot } from '../../interfaces/leapbot.interface';
import { AuthService } from '../auth/auth.service';
import { ClusterService } from '../context/cluster.service';
import { RouteMetadataService } from '../navigation/route-metadata.service';

/**
 * Identifies the running bundle: the hash in `main-<hash>.js` of a production build, or the build
 * mode when there is no hashed bundle (development server).
 */
function readBuildId(): string {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const script of scripts) {
    const match = /\/main-([A-Za-z0-9]+)\.js/.exec(script.getAttribute('src') ?? '');
    if (match) return match[1];
  }
  return environment.production ? 'production' : 'development';
}

/**
 * Describes where the user is in Fury so support can reproduce a problem: route, tool,
 * environment, role, build, browser and viewport. The tenant id is left out until it is confirmed
 * that LeapBot may receive it; the user's e-mail, id, tokens and form values are never included.
 */
@Injectable({ providedIn: 'root' })
export class LeapBotContextService {
  private readonly router = inject(Router);
  private readonly routeMetadata = inject(RouteMetadataService);
  private readonly cluster = inject(ClusterService);
  private readonly auth = inject(AuthService);

  /** Current snapshot of the user's situation. */
  snapshot(): LeapBotContextSnapshot {
    return {
      route: this.router.url.split(/[?#]/)[0],
      toolName: this.routeMetadata.getFeatureName(),
      environment: this.cluster.activeCluster().clusterName,
      role: this.auth.userRole(),
      appBuild: readBuildId(),
      browser: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
    };
  }

  /** Builds the `context.json` file to attach to a message. */
  buildFile(): File {
    return new File([JSON.stringify(this.snapshot(), null, 2)], 'context.json', { type: 'application/json' });
  }
}
